import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  FormControl,
  FormLabel,
  Stack,
} from '@mui/joy';
import {
  DownloadSimple as Download,
  Printer as Print,
  CaretDown as ExpandMore,
  CaretUp as ExpandLess,
  TrendUp as TrendingUp,
  TrendDown as TrendingDown,
} from '@phosphor-icons/react';
import { apiAccountingClient } from '../../../lib/apiAccounting';
import type { BalanceSheet as BalanceSheetType, AccountBalance } from '../../../types/accounting';

export const BalanceSheet: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [balanceSheet, setBalanceSheet] = useState<BalanceSheetType | null>(null);
  const [comparativeSheet, setComparativeSheet] = useState<BalanceSheetType | null>(null);
  
  // Report parameters
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [compareDate, setCompareDate] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['assets', 'liabilities', 'equity']));
  
  useEffect(() => {
    fetchBalanceSheet();
  }, [asOfDate]);

  useEffect(() => {
    if (showComparison && compareDate) {
      fetchComparativeSheet();
    }
  }, [compareDate, showComparison]);

  const fetchBalanceSheet = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiAccountingClient.getBalanceSheet({
        as_of_date: asOfDate,
      });
      // Sanitize numeric values to prevent NaN
      if (response) {
        response.total_liabilities = isNaN(response.total_liabilities) ? 0 : response.total_liabilities;
        response.total_equity = isNaN(response.total_equity) ? 0 : response.total_equity;
        response.total_assets = isNaN(response.total_assets) ? 0 : response.total_assets;
      }
      setBalanceSheet(response);
    } catch (err) {
      console.error('Failed to fetch balance sheet:', err);
      setError('Failed to load balance sheet. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchComparativeSheet = async () => {
    if (!compareDate) return;
    
    try {
      const response = await apiAccountingClient.getBalanceSheet({
        as_of_date: compareDate,
      });
      // Sanitize numeric values to prevent NaN
      if (response) {
        response.total_liabilities = isNaN(response.total_liabilities) ? 0 : response.total_liabilities;
        response.total_equity = isNaN(response.total_equity) ? 0 : response.total_equity;
        response.total_assets = isNaN(response.total_assets) ? 0 : response.total_assets;
      }
      setComparativeSheet(response);
    } catch (err) {
      console.error('Failed to fetch comparative balance sheet:', err);
    }
  };

  const handleExportExcel = async () => {
    try {
      const blob = await apiAccountingClient.exportBalanceSheet({
        as_of_date: asOfDate,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `balance_sheet_${asOfDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export:', err);
      alert('Failed to export balance sheet');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || isNaN(amount)) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const calculateChange = (current: number, previous: number | undefined) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / Math.abs(previous)) * 100;
    return change;
  };

  const renderAccountRow = (account: AccountBalance, compareAccount?: AccountBalance, indent: number = 0) => {
    const change = compareAccount ? calculateChange(account.balance, compareAccount.balance) : null;
    
    return (
      <tr key={account.account_number}>
        <td style={{ paddingLeft: `${indent * 20 + 8}px` }}>
          <Typography level="body-sm" sx={{ fontWeight: account.is_total ? 'bold' : 'normal' }}>
            {account.account_number} - {account.account_name}
          </Typography>
        </td>
        <td align="right">
          <Typography level="body-sm" sx={{ fontWeight: account.is_total ? 'bold' : 'normal' }}>
            {formatCurrency(account.balance)}
          </Typography>
        </td>
        {showComparison && (
          <>
            <td align="right">
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {formatCurrency(compareAccount?.balance)}
              </Typography>
            </td>
            <td align="right">
              {change !== null && (
                <Chip
                  size="sm"
                  color={change > 0 ? 'success' : change < 0 ? 'danger' : 'neutral'}
                  startDecorator={change > 0 ? <TrendingUp /> : change < 0 ? <TrendingDown /> : null}
                >
                  {change.toFixed(1)}%
                </Chip>
              )}
            </td>
          </>
        )}
      </tr>
    );
  };

  const renderSection = (
    title: string,
    accounts: AccountBalance[],
    compareAccounts: AccountBalance[] | undefined,
    total: number,
    compareTotal: number | undefined,
    sectionKey: string
  ) => {
    const isExpanded = expandedSections.has(sectionKey);
    const change = compareTotal ? calculateChange(total, compareTotal) : null;

    return (
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1,
            bgcolor: 'background.level1',
            borderRadius: 'sm',
            cursor: 'pointer',
          }}
          onClick={() => toggleSection(sectionKey)}
        >
          <Typography level="title-md" sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton size="sm" sx={{ mr: 1 }}>
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography level="title-md">
              {formatCurrency(total)}
            </Typography>
            {showComparison && change !== null && (
              <Chip
                size="sm"
                color={change > 0 ? 'success' : change < 0 ? 'danger' : 'neutral'}
                startDecorator={change > 0 ? <TrendingUp /> : change < 0 ? <TrendingDown /> : null}
              >
                {change.toFixed(1)}%
              </Chip>
            )}
          </Box>
        </Box>

        {isExpanded && (
          <Box sx={{ overflowX: 'auto', mt: 2 }}>
            <Table size="sm" sx={{ minWidth: { xs: 480, md: 0 } }}>
              <tbody>
                {accounts.map((account) => {
                  const compareAccount = compareAccounts?.find(
                    (ca) => ca.account_number === account.account_number
                  );
                  return renderAccountRow(account, compareAccount, account.level || 0);
                })}
              </tbody>
            </Table>
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !balanceSheet) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        {error || 'Failed to load balance sheet'}
      </Alert>
    );
  }

  const totalChange = comparativeSheet
    ? calculateChange(
        balanceSheet.total_assets,
        comparativeSheet.total_assets
      )
    : null;

  return (
    <Box className="balance-sheet-report">
      {/* Header Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 1.5, mb: 3 }}>
            <Box>
              <Typography level="h2" sx={{ mb: 1, fontSize: { xs: '1.5rem', md: undefined } }}>
                Balance Sheet
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Statement of Financial Position as of {new Date(asOfDate).toLocaleDateString()}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ '& > button': { flex: { xs: 1, md: '0 0 auto' } } }}>
              <Button
                startDecorator={<Download />}
                variant="outlined"
                color="primary"
                onClick={handleExportExcel}
              >
                Export Excel
              </Button>
              <Button
                startDecorator={<Print />}
                variant="outlined"
                color="primary"
                onClick={handlePrint}
              >
                Print
              </Button>
            </Stack>
          </Box>

          {/* Report Parameters */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
            <FormControl>
              <FormLabel>As of Date</FormLabel>
              <input
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel>
                <input
                  type="checkbox"
                  checked={showComparison}
                  onChange={(e) => setShowComparison(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                Compare with
              </FormLabel>
              {showComparison && (
                <input
                  type="date"
                  value={compareDate || ''}
                  onChange={(e) => setCompareDate(e.target.value)}
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                  }}
                />
              )}
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {/* Balance Sheet Content */}
      <Card>
        <CardContent>
          {/* Assets Section */}
          {renderSection(
            'ASSETS',
            balanceSheet.assets,
            comparativeSheet?.assets,
            balanceSheet.total_assets,
            comparativeSheet?.total_assets,
            'assets'
          )}

          <Divider sx={{ my: 2 }} />

          {/* Liabilities Section */}
          {renderSection(
            'LIABILITIES',
            balanceSheet.liabilities,
            comparativeSheet?.liabilities,
            balanceSheet.total_liabilities,
            comparativeSheet?.total_liabilities,
            'liabilities'
          )}

          <Divider sx={{ my: 2 }} />

          {/* Equity Section */}
          {renderSection(
            'EQUITY',
            balanceSheet.equity,
            comparativeSheet?.equity,
            balanceSheet.total_equity,
            comparativeSheet?.total_equity,
            'equity'
          )}

          <Divider sx={{ my: 3 }} />

          {/* Totals */}
          <Box sx={{ bgcolor: 'background.level1', p: 2, borderRadius: 'sm' }}>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography level="title-lg">Total Assets</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography level="title-lg">
                    {formatCurrency(balanceSheet.total_assets)}
                  </Typography>
                  {showComparison && totalChange !== null && (
                    <Chip
                      color={totalChange > 0 ? 'success' : totalChange < 0 ? 'danger' : 'neutral'}
                      startDecorator={totalChange > 0 ? <TrendingUp /> : totalChange < 0 ? <TrendingDown /> : null}
                    >
                      {totalChange.toFixed(1)}%
                    </Chip>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography level="title-lg">Total Liabilities & Equity</Typography>
                <Typography level="title-lg">
                  {formatCurrency((balanceSheet.total_liabilities || 0) + (balanceSheet.total_equity || 0))}
                </Typography>
              </Box>
              {Math.abs((balanceSheet.total_assets || 0) - ((balanceSheet.total_liabilities || 0) + (balanceSheet.total_equity || 0))) > 0.01 && (
                <Alert color="warning" sx={{ mt: 2 }}>
                  Balance sheet is out of balance by {formatCurrency(
                    Math.abs((balanceSheet.total_assets || 0) - ((balanceSheet.total_liabilities || 0) + (balanceSheet.total_equity || 0)))
                  )}
                </Alert>
              )}
            </Stack>
          </Box>

          {/* Report Metadata */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
              Generated on {new Date().toLocaleString()} • 
              Report Period: As of {new Date(asOfDate).toLocaleDateString()}
              {showComparison && compareDate && ` • Comparing with ${new Date(compareDate).toLocaleDateString()}`}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Print Styles */}
      <style>{`
        @media print {
          .balance-sheet-report {
            padding: 20px;
          }
          button, .MuiButton-root {
            display: none !important;
          }
          input[type="date"], input[type="checkbox"] {
            display: none !important;
          }
        }
      `}</style>
    </Box>
  );
};